/**
 * Groq AI client using API key auth
 */

import type {
  AIClient,
  ChatCompletionRequest,
  ChatCompletionResponse,
  GenerateStoryRequest,
  GenerateStoryResponse,
} from "@/lib/ai/types.ts";
import { PROVIDERS } from "@/lib/ai/config.ts";

export class GroqClient implements AIClient {
  provider = "groq" as const;
  accessToken: string;

  constructor(accessToken: string) {
    this.accessToken = accessToken;
  }

  /**
   * Send chat completion request via server proxy
   */
  async chatCompletion(
    request: ChatCompletionRequest,
  ): Promise<ChatCompletionResponse> {
    const response = await fetch(`/api/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        provider: "groq",
        accessToken: this.accessToken,
        ...request,
      }),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Groq API error: ${response.status} - ${error}`);
    }

    return await response.json();
  }

  /**
   * Generate story elements
   */
  async generateStory(
    request: GenerateStoryRequest,
  ): Promise<GenerateStoryResponse> {
    const systemPrompt = `You are a creative storyteller for children aged ${
      request.context.ageRange || "6-12"
    }. Generate story elements in ${request.context.language}. Return valid JSON only.`;

    const userPrompt = this.buildStoryPrompt(request);

    const completion = await this.chatCompletion({
      model: PROVIDERS.groq.defaultModel,
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt },
      ],
      temperature: 0.9,
      max_tokens: 1000,
    });

    const content = completion.choices[0]?.message?.content || "{}";
    return JSON.parse(content);
  }

  /**
   * Test connection to Groq API
   */
  async testConnection(): Promise<boolean> {
    try {
      await this.chatCompletion({
        model: PROVIDERS.groq.defaultModel,
        messages: [{ role: "user", content: "Hello" }],
        max_tokens: 5,
      });
      return true;
    } catch {
      return false;
    }
  }

  private buildStoryPrompt(request: GenerateStoryRequest): string {
    const { element } = request;
    if (element === "full_story") {
      return "Generate a complete short story with character, location, quest, and skills.";
    }
    return `Generate a ${element} for a children's story. Return JSON: { "elements": [{ "type": "${element}", "name": "...", "description": "...", "attributes": {...} }] }`;
  }
}
